import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Stack,
  Button,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import PaymentRoundedIcon from '@mui/icons-material/PaymentRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import paymentService from '../../../infrastructure/services/paymentService';
import walletService from '../../../infrastructure/services/walletService';
import { getCookie } from '../../../infrastructure/utils/cookieStorage';
import SuccessModal from '../SuccessModal/SuccessModal';

export default function PaymentModal({ open, onClose, exam, onPaymentSuccess }) {
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [availablePoints, setAvailablePoints] = useState(null);

  useEffect(() => {
    if (open) {
      const fetchBalance = async () => {
        try {
          const walletData = await walletService.getWalletBalance();
          if (walletData && walletData.balance !== undefined) {
            setAvailablePoints(walletData.balance);
          }
        } catch (err) {
          console.error('Failed to load wallet balance:', err);
        }
      };
      fetchBalance();
    }
  }, [open]);

  // Promo Code States
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoSuccessMsg, setPromoSuccessMsg] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState('');
  const [discountedAmount, setDiscountedAmount] = useState(null);
  const [promoDiscountText, setPromoDiscountText] = useState('');
  const [promoExpanded, setPromoExpanded] = useState(false);

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;

    setPromoLoading(true);
    setPromoError('');
    setPromoSuccessMsg('');

    const studentId = getCookie('studentId') || '';

    try {
      const response = await paymentService.validatePromoCode({
        code: promoCode.trim(),
        classId: exam.classId,
        studentId
      });

      if (response && response.isSuccessful) {
        const content = response.content;
        if (content && content.valid) {
          setAppliedPromoCode(promoCode.trim());
          setPromoSuccessMsg(response.message || 'Promo code applied successfully!');

          const originalAmount = Number(exam?.fee || 0);
          const discountAmt = Number(content.discountAmount || 0);
          
          let calculatedAmount = originalAmount;
          let discountText = '';
          if (content.discountType === 'percentage') {
            calculatedAmount = originalAmount - (originalAmount * (discountAmt / 100));
            discountText = `${discountAmt}% off`;
          } else if (content.discountType === 'fixed') {
            calculatedAmount = Math.max(0, originalAmount - discountAmt);
            discountText = `${Number(discountAmt).toFixed(2)} points off`;
          } else {
            calculatedAmount = Math.max(0, originalAmount - discountAmt);
            discountText = `${Number(discountAmt).toFixed(2)} points off`;
          }
          
          setDiscountedAmount(calculatedAmount);
          setPromoDiscountText(discountText);
        } else {
          setPromoError(response.message || 'Promo code is not valid.');
        }
      } else {
        setPromoError(response?.message || 'Invalid promo code.');
      }
    } catch (err) {
      setPromoError(err.message || 'Failed to validate promo code.');
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    setAppliedPromoCode('');
    setPromoCode('');
    setDiscountedAmount(null);
    setPromoDiscountText('');
    setPromoSuccessMsg('');
    setPromoError('');
  };

  const handleProcessPayment = async () => {
    if (!exam || !exam.classId) {
      setErrorMessage('Invalid class details.');
      return;
    }

    setPaymentLoading(true);
    setErrorMessage('');

    const payload = {
      year: Number(exam.feeYear || new Date().getFullYear()),
      month: Number(exam.feeMonth || (new Date().getMonth() + 1)),
      amount: Number(discountedAmount !== null ? discountedAmount : (exam.fee || 0)),
      status: 'overdue',
      paymentMethod: 'points',
      ...(appliedPromoCode ? { promoCode: appliedPromoCode } : {})
    };

    try {
      const response = await paymentService.payClassFee(exam.classId, payload);
      if (response && response.isSuccessful) {
        setPaymentSuccess(true);
        if (onPaymentSuccess) {
          onPaymentSuccess(exam.classExamId);
        }
      } else {
        setErrorMessage(response?.message || 'Payment failed. Please try again.');
      }
    } catch (err) {
      setErrorMessage(err.message || 'An error occurred during payment.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleClose = (event, reason) => {
    if (reason && reason === 'backdropClick') return;
    if (paymentLoading) return;
    setPaymentSuccess(false);
    setErrorMessage('');
    setPromoCode('');
    setPromoError('');
    setPromoSuccessMsg('');
    setAppliedPromoCode('');
    setDiscountedAmount(null);
    setPromoDiscountText('');
    setPromoExpanded(false);
    onClose();
  };

  return (
    <>
      <Dialog
        open={open && !paymentSuccess}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '32px !important',
            p: 1,
            border: '1.5px solid #e2e8f0',
            boxShadow: '0 24px 64px rgba(0,0,0,0.08)',
            fontFamily: 'Poppins, sans-serif',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: 'Poppins', fontWeight: 600, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentRoundedIcon sx={{ color: '#FF502D' }} />
          Class Fee Payment
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: '13px', color: '#64748b', fontFamily: 'Poppins', mb: 2 }}>
              Please pay the fee for the class to unlock and attempt this exam.
            </Typography>

            {errorMessage && (
              <Typography
                sx={{
                  color: '#ef4444',
                  fontSize: '12px',
                  fontFamily: 'Poppins',
                  mb: 2,
                  bgcolor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '12px',
                  p: 1.5,
                  textAlign: 'center',
                  fontWeight: 600
                }}
              >
                {errorMessage}
              </Typography>
            )}

            {/* Class Info Box */}
            <Box
              sx={{
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '16px',
                p: 2,
                mb: 0.5
              }}
            >
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, fontFamily: 'Poppins' }}>
                    Subject
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#1e293b', fontWeight: 600, fontFamily: 'Poppins' }}>
                    {exam?.subjectName}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, fontFamily: 'Poppins' }}>
                    Class
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#1e293b', fontWeight: 600, fontFamily: 'Poppins' }}>
                    {exam?.className}
                  </Typography>
                </Box>
                {exam?.feeMonth && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, fontFamily: 'Poppins' }}>
                      Payable Month
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#1e293b', fontWeight: 600, fontFamily: 'Poppins' }}>
                      {new Date(exam?.feeYear || 2026, (exam?.feeMonth || 1) - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: discountedAmount !== null ? 'flex-start' : 'center', borderTop: '1px dashed #cbd5e1', pt: 1, mt: 1 }}>
                  <Typography sx={{ fontSize: '13px', color: '#1e293b', fontWeight: 600, fontFamily: 'Poppins', mt: discountedAmount !== null ? 0.5 : 0 }}>
                    Class Fee (Required Points)
                  </Typography>
                  {discountedAmount !== null ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                      <Typography sx={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '12px', fontWeight: 500, fontFamily: 'Poppins' }}>
                        {exam?.fee ? `${Number(exam.fee).toFixed(2)} points` : '0.00 points'}
                      </Typography>
                      <Typography sx={{ color: '#10B981', fontSize: '15px', fontWeight: 700, fontFamily: 'Poppins', mt: 0.25 }}>
                        {Number(discountedAmount).toFixed(2)} points
                      </Typography>
                      {promoDiscountText && (
                        <Typography sx={{ color: '#10B981', fontSize: '11px', fontWeight: 600, fontFamily: 'Poppins', mt: 0.25 }}>
                          {promoDiscountText}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '14px', color: '#FF502D', fontWeight: 700, fontFamily: 'Poppins' }}>
                      {exam?.fee ? `${Number(exam.fee).toFixed(2)} points` : 'N/A'}
                    </Typography>
                  )}
                </Box>
                {availablePoints !== null && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #cbd5e1', pt: 1, mt: 1 }}>
                    <Typography sx={{ fontSize: '13px', color: '#64748b', fontWeight: 600, fontFamily: 'Poppins' }}>
                      Available Points
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#10B981', fontWeight: 700, fontFamily: 'Poppins' }}>
                      {Number(availablePoints).toFixed(2)} points
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            {/* Promo Code Box */}
            <Box sx={{ mt: 2 }}>
              <Box
                onClick={() => setPromoExpanded(!promoExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.75,
                  cursor: 'pointer',
                  userSelect: 'none',
                  py: 0.5,
                  mb: promoExpanded ? 2 : 0,
                  transition: 'all 0.2s',
                  '&:hover': { opacity: 0.85 }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 17,
                    height: 17,
                    borderRadius: '50%',
                    bgcolor: '#FF502D',
                    color: '#ffffff'
                  }}
                >
                  <PercentRoundedIcon sx={{ fontSize: '10px', color: '#ffffff' }} />
                </Box>
                <Typography sx={{ color: '#2563EB', fontWeight: 600, fontSize: '13px', fontFamily: 'Poppins' }}>
                  Apply promo code
                </Typography>
                {promoExpanded ? (
                  <KeyboardArrowUpRoundedIcon sx={{ color: '#2563EB', fontSize: 16 }} />
                ) : (
                  <KeyboardArrowDownRoundedIcon sx={{ color: '#2563EB', fontSize: 16 }} />
                )}
              </Box>

              {promoExpanded && (
                <Box sx={{ animation: 'fadeIn 0.2s ease-out' }}>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'stretch' }}>
                    <TextField
                      fullWidth
                      placeholder="Enter promo code"
                      size="small"
                      value={promoCode}
                      disabled={promoLoading || paymentLoading || !!appliedPromoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value);
                        setPromoError('');
                        setPromoSuccessMsg('');
                      }}
                      error={!!promoError}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {promoLoading ? (
                              <CircularProgress size={16} sx={{ color: '#FF502D' }} />
                            ) : !!appliedPromoCode ? (
                              <CheckCircleRoundedIcon sx={{ color: '#10B981', fontSize: 20 }} />
                            ) : null}
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          fontFamily: 'Poppins',
                          fontSize: '13px',
                          bgcolor: '#ffffff',
                          height: '40px',
                          '& fieldset': {
                            borderColor: !!appliedPromoCode ? '#10B981' : (!!promoError ? '#ef4444' : '#cbd5e1'),
                            borderWidth: !!appliedPromoCode || !!promoError ? '1.5px' : '1px'
                          },
                          '&:hover fieldset': {
                            borderColor: !!appliedPromoCode ? '#10B981' : (!!promoError ? '#ef4444' : '#94a3b8')
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: !!appliedPromoCode ? '#10B981' : (!!promoError ? '#ef4444' : '#2563EB'),
                            borderWidth: '1.5px'
                          }
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      disabled={(!promoCode.trim() && !appliedPromoCode) || promoLoading || paymentLoading}
                      onClick={appliedPromoCode ? handleRemovePromoCode : handleApplyPromoCode}
                      sx={{
                        bgcolor: appliedPromoCode ? '#ef4444' : '#2563EB',
                        color: '#ffffff',
                        fontWeight: 600,
                        fontFamily: 'Poppins',
                        fontSize: '13px',
                        textTransform: 'none',
                        borderRadius: '12px',
                        px: 3,
                        height: '40px',
                        boxShadow: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          bgcolor: appliedPromoCode ? '#dc2626' : '#1d4ed8',
                          boxShadow: 'none'
                        },
                        '&.Mui-disabled': {
                          bgcolor: '#e2e8f0',
                          color: '#94a3b8',
                          opacity: 1
                        }
                      }}
                    >
                      {appliedPromoCode ? 'Remove' : 'Apply'}
                    </Button>
                  </Box>

                  {/* Success / Error Messages below */}
                  {promoError && (
                    <Typography sx={{ color: '#ef4444', fontSize: '12px', fontWeight: 500, fontFamily: 'Poppins', mt: 1, ml: 1 }}>
                      {promoError}
                    </Typography>
                  )}

                  {promoSuccessMsg && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, ml: 0.5 }}>
                      <CheckCircleRoundedIcon sx={{ fontSize: 16, color: '#10B981' }} />
                      <Typography sx={{ color: '#10B981', fontSize: '12px', fontWeight: 500, fontFamily: 'Poppins' }}>
                        {promoSuccessMsg}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
            <Button
              variant="outlined"
              disabled={paymentLoading}
              onClick={handleClose}
              sx={{
                flex: 1,
                borderColor: '#cbd5e1',
                color: '#64748b',
                fontWeight: 600,
                fontFamily: 'Poppins',
                fontSize: '13px',
                textTransform: 'none',
                borderRadius: '12px',
                height: '40px',
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: 'rgba(0,0,0,0.02)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={paymentLoading || (promoCode.trim() !== '' && !appliedPromoCode)}
              onClick={handleProcessPayment}
              startIcon={paymentLoading ? <CircularProgress size={16} sx={{ color: '#ffffff' }} /> : <PaymentRoundedIcon />}
              sx={{
                flex: 1.5,
                bgcolor: '#FF502D',
                color: '#ffffff',
                fontWeight: 600,
                fontFamily: 'Poppins',
                fontSize: '13px',
                textTransform: 'none',
                borderRadius: '12px',
                height: '40px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: '#e64525',
                  boxShadow: 'none'
                }
              }}
            >
              {paymentLoading ? 'Processing...' : 'Pay Now'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <SuccessModal
        isOpen={paymentSuccess}
        onClose={handleClose}
        title="Payment Successful!"
        description="The exam has been successfully unlocked. You can now start the exam immediately."
        actionText="Close"
        onAction={handleClose}
      />
    </>
  );
}
